import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { FiTrendingUp } from "react-icons/fi";
import {
  containsRestrictedKeyword,
  filterSafeSearchValues,
} from "../../utils/restrictedKeywords";

const TrendingSearch = ({ className = "", limit = 8 }) => {
  const [searches, setSearches] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let isMounted = true;

    const normalizeConfiguredSearches = (items = []) =>
      (Array.isArray(items) ? items : [])
        .map((item, index) => {
          const keyword =
            typeof item === "string" ? item : String(item?.keyword || "");
          const trimmedKeyword = keyword.trim();
          const searchValue =
            typeof item === "string"
              ? trimmedKeyword
              : String(item?.searchValue || item?.keyword || "").trim();
          const displayOrder = Number.isFinite(item?.displayOrder)
            ? item.displayOrder
            : Number.isFinite(item?.order)
              ? item.order
              : index;
          const active =
            typeof item === "string"
              ? true
              : item?.active !== false && item?.visible !== false;

          return {
            keyword: trimmedKeyword,
            searchValue,
            normalizedQuery: searchValue.toLowerCase(),
            displayOrder,
            active,
          };
        })
        .filter(
          (item) =>
            item.active &&
            item.keyword &&
            item.searchValue &&
            !containsRestrictedKeyword(item.keyword) &&
            !containsRestrictedKeyword(item.searchValue),
        )
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .slice(0, limit);

    const fetchTrendingSearches = async () => {
      try {
        const masterResponse = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/config/master-data?_=${Date.now()}`,
          {
            headers: {
              "Cache-Control": "no-cache",
            },
          },
        );
        const configuredSearches = normalizeConfiguredSearches(
          masterResponse?.data?.data?.filters?.trendingKeywords,
        );

        if (configuredSearches.length > 0) {
          if (isMounted) {
            setSearches(configuredSearches);
          }
          return;
        }

        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/search/trending?limit=${limit}&_=${Date.now()}`,
          {
            headers: {
              "Cache-Control": "no-cache",
            },
          },
        );
        const nextSearches = Array.isArray(response?.data?.searches)
          ? response.data.searches
          : [];

        if (isMounted) {
          setSearches(
            nextSearches.filter(
              (item) =>
                !containsRestrictedKeyword(item?.keyword || item?.normalizedQuery) &&
                !containsRestrictedKeyword(item?.searchValue || item?.keyword),
            ),
          );
        }
      } catch (error) {
        if (isMounted) {
          setSearches([]);
        }
      }
    };

    fetchTrendingSearches();

    const refreshTrendingSearches = () => {
      if (isMounted) {
        setRefreshKey((prev) => prev + 1);
      }
    };

    const handleStorage = (event) => {
      if (event.key === "trendingKeywordsUpdatedAt") {
        refreshTrendingSearches();
      }
    };

    window.addEventListener("trendingKeywordsUpdated", refreshTrendingSearches);
    window.addEventListener("storage", handleStorage);
    window.addEventListener("focus", refreshTrendingSearches);

    return () => {
      isMounted = false;
      window.removeEventListener("trendingKeywordsUpdated", refreshTrendingSearches);
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("focus", refreshTrendingSearches);
    };
  }, [limit, refreshKey]);

  if (searches.length === 0) {
    return null;
  }

  return (
    <div className={`trending-search ${className}`}>
      <div className="trending-search-label">
        <FiTrendingUp aria-hidden="true" />
        <span>Trending</span>
      </div>
      <div className="trending-search-list" aria-label="Trending searches">
        {searches.map((item) => {
          const keyword = String(item.keyword || item.normalizedQuery || "").trim();
          const searchValue = String(item.searchValue || keyword).trim();
          if (
            !keyword ||
            filterSafeSearchValues([keyword, searchValue]).length !== 2
          ) {
            return null;
          }

          return (
            <Link
              key={item.normalizedQuery || searchValue || keyword}
              to={`/search?term=${encodeURIComponent(searchValue)}`}
              className="trending-search-pill"
              onClick={() => {
                try {
                  sessionStorage.setItem("shotkut-search-source", "trending");
                } catch (error) {
                  // Ignore storage failures.
                }
              }}
            >
              {keyword}
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default TrendingSearch;
